/**
 * 사용자 관리.
 *
 * 메일 발송 수단이 없어 초대 링크 대신 **임시 비밀번호**를 쓴다. 만들면 평문이
 * 한 번 보이고 다시는 볼 수 없다 — 저장하지 않기 때문이다. 그 사실을 화면에서
 * 분명히 말해야 관리자가 창을 닫기 전에 옮겨 적는다.
 */
import { useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Copy, Check, Pencil, UserPlus } from 'lucide-react';
import type { AccountUser, Group, Me, Role, SignageOneClient } from '../api/client.js';
import { Badge, Empty, ErrorNote, ItemTitle, Loading, Modal, Panel, RelTime } from '../components/ui.js';
import { HelpTip } from '../components/HelpTip.js';
import { copyText } from '../clipboard.js';
import { useAction, useAsync } from '../hooks.js';
import { ROLE_LABEL, roleLabel } from '../roles.js';
import { MoreMenu } from '../components/MoreMenu.js';
import { josa } from '../josa.js';
import { Disclosure } from '../components/Disclosure.js';
import { confirmDialog } from '../components/ConfirmDialog.js';
import { IconButton } from '../components/IconButton.js';
import { ColumnHead, FilterMenu, FilterNote, useTableSort } from '../components/TableHead.js';
import { applyFilters, filterOptions, fixedOrder, sortRows, type Filters, type SortDir, type SortValue } from '../components/tableView.js';
import { useUrlQuery } from '../components/urlState.js';
import { Pager, usePaging } from '../components/Pager.js';
import { paginate } from '../components/paging.js';

const ROLE_HELP: Record<Role, string> = {
  viewer: '조회만 가능',
  auditor: '조회, 리포트·감사 로그 확인',
  'content-manager': '콘텐츠 등록·수정',
  admin: '고객사 관리 등급. 편성·화면 운영과 자기 팀 계정 관리',
  system: '공급사·개발 등급. 긴급 오버라이드·릴리스·서버 설정·플러그인을 포함한 전체 권한',
};

const ROLE_ORDER: Role[] = ['viewer', 'auditor', 'content-manager', 'admin', 'system'];

/**
 * 이 사람이 줄 수 있는 역할.
 *
 * **자기보다 큰 권한은 만들어 낼 수 없다.** 관리자(`admin`)에게 사용자 관리를 떼어 준 것은 자기 팀
 * 계정을 돌보라는 뜻이지 시스템 계정을 늘리라는 뜻이 아니다 — 시스템 계정을 만들 수 있으면 그
 * 계정으로 로그인해 무엇이든 할 수 있으니, 사용자 관리를 준 것이 곧 시스템 권한을 준 것이 된다.
 *
 * 서버가 같은 규칙을 다시 검사한다(`routes/users.ts`). 여기서 목록을 줄이는 것은 고를 수
 * 없는 것을 늘어놓지 않기 위해서다.
 */
export function assignableRoles(capabilities: readonly string[]): Role[] {
  if (capabilities.includes('system')) return ROLE_ORDER;
  return ROLE_ORDER.filter((role) => role !== 'system');
}

export const USER_SORT_KEYS = ['name', 'login'] as const;
export type UserSortKey = (typeof USER_SORT_KEYS)[number];
/** 상태 필터의 값 — 표의 상태 칸과 같은 순서로 판정한다(비활성 → 잠김 → 임시 비밀번호 → 사용 중). */
export type UserState = 'disabled' | 'locked' | 'temporary' | 'active';
export const USER_STATE_LABEL: Readonly<Record<UserState, string>> = {
  active: '사용 중',
  temporary: '임시 비밀번호',
  locked: '잠김',
  disabled: '비활성',
};

export function userState(user: Pick<AccountUser, 'status' | 'locked' | 'must_change_password'>): UserState {
  if (user.status === 'disabled') return 'disabled';
  if (user.locked) return 'locked';
  return user.must_change_password ? 'temporary' : 'active';
}

export function userSortValue(user: AccountUser, key: UserSortKey): SortValue {
  if (key === 'name') return user.name || null;
  // 한 번도 로그인하지 않은 계정은 "기록 없음" — 오래된 순 맨 앞에 와야 정리할 계정이 보인다.
  return user.last_login_at ? Date.parse(user.last_login_at) : 0;
}

export function UsersScreen({ client, me }: { client: SignageOneClient; me: Me | null }): ReactNode {
  const users = useAsync(() => client.listUsers(), [client]);
  const groupList = useAsync(() => client.listGroups(), [client]);
  const groups = groupList.data?.items ?? [];
  const action = useAction();
  const [creating, setCreating] = useState(false);
  const [scoping, setScoping] = useState<AccountUser | null>(null);
  /** 이름을 바꾸는 계정 — 예전에는 만들 때 적은 이름을 고칠 길이 없었다(2026-10-20). */
  const [renaming, setRenaming] = useState<AccountUser | null>(null);
  const [issued, setIssued] = useState<{ email: string; password: string } | null>(null);

  const loaded = users.data?.items ?? [];
  /* 정렬·필터 — 필터는 주소에(`?role=·state=`), 정렬은 브라우저에. */
  const url = useUrlQuery();
  const roleChosen = url.list('role');
  const stateChosen = url.list('state');
  const filters: Filters<'role' | 'state'> = { role: roleChosen, state: stateChosen };
  const [sort, toggleSort] = useTableSort<UserSortKey>('users', USER_SORT_KEYS, { key: 'name', dir: 'asc' });
  const items = useMemo(
    () =>
      sortRows(
        applyFilters(loaded, filters, (user, key) => (key === 'role' ? user.role : userState(user))),
        sort,
        userSortValue,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 필터는 주소에서 매번 새로 읽어 내용으로 본다.
    [loaded, roleChosen.join(','), stateChosen.join(','), sort],
  );
  const filtering = roleChosen.length + stateChosen.length > 0;
  /* 쪽 나누기 — 필터·정렬을 바꾸면 1쪽으로(`Pager`). */
  const paging = usePaging(url, 'users', JSON.stringify([roleChosen, stateChosen, sort.key, sort.dir]));
  const pageSlice = paginate(items, paging.page, paging.size);
  const sorting = (dirLabels: Record<SortDir, string>, firstDir: SortDir = 'asc') => ({
    sort,
    onSort: toggleSort,
    dirLabels,
    firstDir,
  });
  const myEmail = me?.account?.email ?? '';
  const roles = assignableRoles(me?.capabilities ?? []);
  /** 시스템이 아닌 사람(고객사 관리자) — 시스템 계정은 목록에도 오지 않는다(서버가 뺀다). */
  const limited = !(me?.capabilities ?? []).includes('system');

  const update = (user: AccountUser, patch: Parameters<SignageOneClient['updateUser']>[1]): void => {
    void action.run(() => client.updateUser(user.id, patch), users.reload);
  };

  return (
    <div className="screen animate-fade-in">
      {action.error ? <ErrorNote message={action.error} onDismiss={action.clearError} /> : null}
      {users.error ? <ErrorNote message={users.error} /> : null}

      <Panel
        title={`사용자 ${loaded.length}명`}
        className="screen-fill"
        actions={
          <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
            <UserPlus className="h-3.5 w-3.5" strokeWidth={2} />
            사용자 추가
          </button>
        }
      >
        {users.loading && items.length === 0 ? <Loading /> : null}

        {!users.loading && loaded.length === 0 ? (
          <Empty title="사용자가 없습니다" hint="사용자를 추가하면 임시 비밀번호가 한 번 표시됩니다." />
        ) : null}

        {filtering && loaded.length > 0 ? (
          <FilterNote shown={items.length} total={loaded.length} unit="명" onClear={() => url.set({ role: null, state: null })} />
        ) : null}

        {loaded.length > 0 ? (
          <table className="table min-w-[44rem]">
            <thead>
              <tr>
                <ColumnHead label="이름" column="name" sorting={sorting({ asc: '가나다순', desc: '가나다 역순' })} />
                <th>이메일</th>
                <ColumnHead
                  label="역할"
                  column="role"
                  filter={
                    <FilterMenu
                      label="역할"
                      options={filterOptions(
                        loaded,
                        (user) => user.role,
                        (value) => ({ label: roleLabel(value) }),
                        roleChosen,
                        fixedOrder(ROLE_ORDER),
                      )}
                      chosen={roleChosen}
                      onChange={(chosen) => url.set({ role: chosen })}
                    />
                  }
                />
                <th>
                  담당 범위
                  <HelpTip text={SCOPE_HELP} label="담당 범위 도움말" />
                </th>
                <ColumnHead
                  label="상태"
                  column="state"
                  filter={
                    <FilterMenu
                      label="상태"
                      options={filterOptions(
                        loaded,
                        (user) => userState(user),
                        (value) => ({ label: USER_STATE_LABEL[value as UserState] ?? value }),
                        stateChosen,
                        fixedOrder(['active', 'temporary', 'locked', 'disabled']),
                      )}
                      chosen={stateChosen}
                      onChange={(chosen) => url.set({ state: chosen })}
                    />
                  }
                />
                {/* 오래된 순이 먼저다 — 이 열을 누르는 사람은 안 쓰는 계정을 정리하는 중이다. */}
                <ColumnHead label="마지막 로그인" column="login" sorting={sorting({ asc: '오래된 순', desc: '최근 순' })} />
                <th className="col-actions" />
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr className="row-empty">
                  <td colSpan={7}>필터 조건에 맞는 사용자가 없습니다.</td>
                </tr>
              ) : null}
              {pageSlice.rows.map((user) => {
                const isMe = user.email === myEmail;
                /*
                 * 내가 줄 수 없는 역할의 계정(서버가 모르는 옛 값 등). 보이기는 하되(같은
                 * 관리 능력을 가진 사람이 누구인지는 알아야 한다) 역할·상태·비밀번호를 바꾸지 못한다.
                 */
                const untouchable = !roles.includes(user.role);
                return (
                  <tr key={user.id} className={user.status === 'disabled' ? 'row-muted' : undefined}>
                    <td className="cell-strong">
                      {/*
                        이름을 누르면 [⋯] › 이름 바꾸기 와 같다. 사내 계정은 로그인할 때마다 사내 계정 시스템의 이름으로 맞춰지고,
                        바꿀 수 없는 역할의 계정은 고치지 못해 글자로만 둔다.
                      */}
                      <ItemTitle
                        className=""
                        onOpen={
                          !untouchable && user.auth_source !== 'oidc' && !action.pending ? () => setRenaming(user) : undefined
                        }
                        label={`${user.name} 이름 바꾸기`}
                      >
                        {user.name}
                      </ItemTitle>
                      {isMe ? <Badge tone="brand">나</Badge> : null}
                      {user.auth_source === 'oidc' ? (
                        <span title="사내 계정 시스템에서 온 계정 — 역할은 로그인할 때마다 사내 계정 시스템의 값으로 맞춰집니다.">
                          <Badge tone="info">사내 계정</Badge>
                        </span>
                      ) : null}
                    </td>
                    <td className="cell-muted">{user.email}</td>
                    <td>
                      {untouchable ? (
                        <Badge tone="brand">{ROLE_LABEL[user.role]}</Badge>
                      ) : (
                        <select
                          className="input"
                          value={user.role}
                          disabled={action.pending}
                          aria-label={`${user.name} 역할`}
                          onChange={(event) => update(user, { role: event.target.value as Role })}
                        >
                          {roles.map((role) => (
                            <option key={role} value={role}>
                              {ROLE_LABEL[role]}
                            </option>
                          ))}
                        </select>
                      )}
                    </td>
                    <td>
                      <span className="cell-muted">{scopeLabel(user.scope_group_ids ?? [], groups)}</span>
                      {/* 자기 범위는 바꾸지 못한다 — 줄이는 순간 계정 관리를 잃어 되돌릴 수 없다(서버도 막는다). */}
                      {untouchable || isMe || user.role === 'system' ? null : (
                        <IconButton
                          icon={Pencil}
                          label="담당 범위 바꾸기"
                          subject={user.name}
                          className="ml-2"
                          disabled={action.pending}
                          onClick={() => setScoping(user)}
                        />
                      )}
                    </td>
                    <td>
                      {user.status === 'disabled' ? (
                        <Badge tone="neutral">비활성</Badge>
                      ) : user.locked ? (
                        <Badge tone="danger">잠김</Badge>
                      ) : user.must_change_password ? (
                        <Badge tone="warn">임시 비밀번호</Badge>
                      ) : (
                        <Badge tone="ok">사용 중</Badge>
                      )}
                    </td>
                    <td className="cell-muted"><RelTime iso={user.last_login_at} /></td>
                    <td className="col-actions">
                      {untouchable ? (
                        <span className="hint">시스템 계정만 변경 가능</span>
                      ) : (
                        /*
                          계정 작업은 모두 드물고 둘은 되돌리기 어렵다 — 줄마다 글자 단추 셋을 늘어놓지 않고 [⋯] 하나로(점진적 공개).
                          사내 계정은 비밀번호가 없다 — 사내 계정 시스템에서 바꾼다(서버도 거절한다).
                          자기 계정 삭제는 서버도 막지만, 누를 수 있는 항목을 두지 않는다.
                        */
                        <MoreMenu
                          label={`${user.name} 계정 작업`}
                          items={[
                            {
                              /* 사내 계정은 로그인할 때마다 사내 계정 시스템의 이름으로 맞춰진다 — 여기서 바꿔도 되돌아간다. */
                              label: '이름 바꾸기',
                              shown: user.auth_source !== 'oidc',
                              disabled: action.pending,
                              onSelect: () => setRenaming(user),
                            },
                            {
                              label: '비밀번호 재설정',
                              shown: user.auth_source !== 'oidc',
                              disabled: action.pending,
                              onSelect: async () => {
                                const ok = await confirmDialog({
                                  title: '비밀번호를 재설정할까요?',
                                  body: <><strong>{user.name}</strong> — 로그인 중인 세션은 모두 종료됩니다.</>,
                                  confirmLabel: '재설정',
                                  danger: true,
                                });
                                if (!ok) return;
                                void action.run(
                                  () => client.resetUserPassword(user.id),
                                  (result) => {
                                    setIssued({ email: user.email, password: result.temporary_password });
                                    users.reload();
                                  },
                                  );
                              },
                            },
                            {
                              label: user.status === 'active' ? '비활성화' : '활성화',
                              disabled: action.pending,
                              onSelect: () => update(user, { status: user.status === 'active' ? 'disabled' : 'active' }),
                            },
                            {
                              label: '삭제',
                              danger: true,
                              shown: !isMe,
                              disabled: action.pending,
                              onSelect: async () => {
                                const ok = await confirmDialog({
                                  title: '사용자를 삭제할까요?',
                                  body: <><strong>{user.name}</strong>({user.email}) — 되돌릴 수 없습니다.</>,
                                  confirmLabel: '삭제',
                                  danger: true,
                                });
                                if (!ok) return;
                                void action.run(() => client.deleteUser(user.id), users.reload, `${user.name} 계정을 삭제했습니다`);
                              },
                            },
                          ]}
                        />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : null}

        <Pager slice={pageSlice} paging={paging} unit="명" />
      </Panel>

      {/*
        역할 설명도 **줄 수 있는 것만** 적는다.
        
        시스템을 줄 수 없는 사람에게 "시스템 — 전부 할 수 있음" 을 설명해 봐야, 고를 수도
        볼 수도 없는 역할을 하나 알려 주는 것뿐이다. 관리자(admin)에게 시스템 계정은 없는 사람이라는
        규칙(목록에서 빠지고, 지목해도 없다고 답한다)과 화면이 같은 말을 해야 한다.
      */}
      {/* 처음 한 번 읽으면 되는 설명표 — 펼친 채로 열고, 접으면 머리에 역할 수를 남긴다. */}
      <Panel
        title="역할이 할 수 있는 일"
        subtitle={limited ? `역할 ${roles.length}개 · 내가 부여할 수 있는 역할만 표시` : `역할 ${roles.length}개`}
        collapsible
      >
        <ul className="divide-y divide-slate-100">
          {roles.map((role) => (
            <li key={role} className="flex items-baseline gap-3 px-4 py-2">
              <span className="w-24 shrink-0 text-[13.5px] font-medium text-slate-700">{ROLE_LABEL[role]}</span>
              <span className="text-[12.5px] text-slate-500">{ROLE_HELP[role]}</span>
            </li>
          ))}
        </ul>
      </Panel>

      {renaming ? (
        <RenameUserModal
          user={renaming}
          pending={action.pending}
          onClose={() => setRenaming(null)}
          onSave={(name) =>
            void action.run(
              () => client.updateUser(renaming.id, { name }),
              () => {
                setRenaming(null);
                users.reload();
              },
              `계정 이름을 "${name}"${josa(name, '으로/로')} 바꿨습니다`,
            )
          }
        />
      ) : null}

      {scoping ? (
        <ScopeModal
          user={scoping}
          groups={groups}
          pending={action.pending}
          onClose={() => setScoping(null)}
          onSave={(ids) =>
            void action.run(
              () => client.updateUser(scoping.id, { scope_group_ids: ids }),
              () => {
                setScoping(null);
                users.reload();
              },
            )
          }
        />
      ) : null}

      {creating ? (
        <CreateUserModal
          client={client}
          roles={roles}
          groups={groups}
          onClose={() => setCreating(false)}
          onCreated={(result) => {
            setCreating(false);
            if (result) setIssued(result);
            users.reload();
          }}
        />
      ) : null}

      {issued ? <TemporaryPasswordModal issued={issued} onClose={() => setIssued(null)} /> : null}
    </div>
  );
}

/** 담당 범위를 사람이 읽는 말로 — 비어 있으면 전체, 지워진 그룹은 그렇다고. */
export function scopeLabel(ids: readonly string[], groups: readonly Pick<Group, 'id' | 'name'>[]): string {
  if (ids.length === 0) return '전체';
  const names = new Map(groups.map((group) => [group.id, group.name]));
  return ids.map((id) => names.get(id) ?? '(지워진 그룹)').join(', ');
}

const SCOPE_HELP =
  '그룹을 고르면 그 그룹의 화면만 보고 고칩니다. 캠페인·긴급은 그 그룹에만 걸 수 있고, 다른 곳에도 나가는 플레이리스트·콘텐츠는 고치지 못합니다. ' +
  '사용자·환경 설정은 쓸 수 없습니다. 비워 두면 고객사 전체를 맡습니다.';

/** 그룹 고르기 — 체크한 것이 맡을 그룹이다. 아무것도 안 고르면 전체. */
function ScopePicker({
  groups,
  value,
  disabled,
  onChange,
}: {
  groups: readonly Group[];
  value: readonly string[];
  disabled?: boolean;
  onChange: (next: string[]) => void;
}): ReactNode {
  if (groups.length === 0) {
    return <small className="hint">화면 그룹이 없습니다 — [화면 그룹] 메뉴에서 사옥·지점별 그룹을 먼저 만드세요.</small>;
  }
  return (
    <div className="scope-picker">
      {groups.map((group) => (
        <label key={group.id} className="scope-picker-item">
          <input
            type="checkbox"
            checked={value.includes(group.id)}
            disabled={disabled}
            onChange={(event) =>
              onChange(event.target.checked ? [...value, group.id] : value.filter((id) => id !== group.id))
            }
          />
          <span>{group.name}</span>
          <span className="cell-muted">화면 {group.display_count}대</span>
        </label>
      ))}
    </div>
  );
}

/** 계정 이름 바꾸기 — 비운 채 저장하면 칸을 짚고 안내한다. 로그인 아이디(이메일)는 그대로다. */
function RenameUserModal({
  user,
  pending,
  onClose,
  onSave,
}: {
  user: AccountUser;
  pending: boolean;
  onClose: () => void;
  onSave: (name: string) => void;
}): ReactNode {
  const [name, setName] = useState(user.name);
  const [invalid, setInvalid] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <Modal title="계정 이름 바꾸기" onClose={onClose}>
      <form
        className="form"
        onSubmit={(event) => {
          event.preventDefault();
          const next = name.trim();
          if (!next) {
            setInvalid(true);
            inputRef.current?.focus();
            return;
          }
          if (next === user.name) {
            onClose();
            return;
          }
          onSave(next);
        }}
      >
        <label className="field">
          <span>이름</span>
          <input
            ref={inputRef}
            className="input"
            value={name}
            maxLength={120}
            autoFocus
            aria-invalid={invalid || undefined}
            disabled={pending}
            onChange={(event) => {
              setName(event.target.value);
              setInvalid(false);
            }}
          />
          {invalid ? (
            <p className="field-error" role="alert">
              이름을 입력하세요
            </p>
          ) : (
            <small className="hint">로그인 아이디({user.email})는 바뀌지 않습니다.</small>
          )}
        </label>
        <div className="form-actions">
          <button type="button" className="btn" onClick={onClose} disabled={pending}>
            취소
          </button>
          <button type="submit" className="btn btn-primary" disabled={pending}>
            저장
          </button>
        </div>
      </form>
    </Modal>
  );
}

function ScopeModal({
  user,
  groups,
  pending,
  onClose,
  onSave,
}: {
  user: AccountUser;
  groups: readonly Group[];
  pending: boolean;
  onClose: () => void;
  onSave: (ids: string[]) => void;
}): ReactNode {
  const [value, setValue] = useState<string[]>(user.scope_group_ids ?? []);
  return (
    <Modal title={`${user.name} — 담당 범위`} onClose={onClose}>
      <form
        className="form"
        onSubmit={(event) => {
          event.preventDefault();
          onSave(value);
        }}
      >
        <p className="hint">{SCOPE_HELP}</p>
        <ScopePicker groups={groups} value={value} disabled={pending} onChange={setValue} />
        <p className="hint">
          지금: <strong>{scopeLabel(value, groups)}</strong> · 저장하면 그 사람의 다음 요청부터 바로 적용됩니다.
        </p>
        <div className="form-actions">
          <button type="button" className="btn" onClick={onClose} disabled={pending}>
            취소
          </button>
          <button type="submit" className="btn btn-primary" disabled={pending}>
            저장
          </button>
        </div>
      </form>
    </Modal>
  );
}

function CreateUserModal({
  client,
  roles,
  groups,
  onClose,
  onCreated,
}: {
  client: SignageOneClient;
  /** 이 사람이 줄 수 있는 역할. 시스템(admin)이 아니면 '시스템' 이 빠진다. */
  roles: readonly Role[];
  groups: readonly Group[];
  onClose: () => void;
  onCreated: (issued: { email: string; password: string } | null) => void;
}): ReactNode {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<Role>('admin');
  const [scope, setScope] = useState<string[]>([]);
  const action = useAction();

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    void action.run(
      () =>
        client.createUser({
          email: email.trim(),
          name: name.trim(),
          role,
          // 시스템 역할에는 범위가 없다(서버가 거절한다).
          scope_group_ids: role === 'system' ? [] : scope,
        }),
      (result) =>
        onCreated(
          result.temporary_password
            ? { email: result.user.email, password: result.temporary_password }
            : null,
        ),
    );
  };

  return (
    <Modal title="사용자 추가" onClose={onClose}>
      <form className="form" onSubmit={submit}>
        <label className="field">
          <span className="field-label">이름</span>
          <input
            className="input"
            required
            autoFocus
            maxLength={120}
            value={name}
            disabled={action.pending}
            onChange={(event) => setName(event.target.value)}
          />
        </label>
        <label className="field">
          <span className="field-label">이메일</span>
          <input
            className="input"
            type="email"
            required
            value={email}
            disabled={action.pending}
            onChange={(event) => setEmail(event.target.value)}
          />
        </label>
        <label className="field">
          <span className="field-label">역할</span>
          <select
            className="input"
            value={role}
            disabled={action.pending}
            onChange={(event) => setRole(event.target.value as Role)}
          >
            {roles.map((value) => (
              <option key={value} value={value}>
                {ROLE_LABEL[value]}
              </option>
            ))}
          </select>
          <small className="hint">{ROLE_HELP[role]}</small>
        </label>
        {/*
          담당 범위는 대부분 비워 둔다(고객사 전체) — 그룹 목록을 통째로 펼쳐 두면 "골라야 하나" 로 읽힌다.
          [담당 범위] 를 펼쳐야 고른다(점진적 공개). 접힌 머리에 지금 값을 적는다.
        */}
        {role === 'system' ? null : (
          <Disclosure title="담당 범위" summary={scopeLabel(scope, groups)}>
            <div className="field">
              <span className="field-label">
                맡길 그룹 <HelpTip text={SCOPE_HELP} label="담당 범위 도움말" />
              </span>
              <ScopePicker groups={groups} value={scope} disabled={action.pending} onChange={setScope} />
              <small className="hint">비워 두면 고객사 전체 · 지금: {scopeLabel(scope, groups)}</small>
            </div>
          </Disclosure>
        )}

        {action.error ? <ErrorNote message={action.error} onDismiss={action.clearError} /> : null}

        <p className="hint">
          임시 비밀번호는 <strong>한 번만</strong> 표시됩니다. 받은 사람은 첫 로그인 때 비밀번호를 변경해야 합니다.
        </p>

        <div className="form-actions">
          <button type="button" className="btn" onClick={onClose} disabled={action.pending}>
            취소
          </button>
          <button type="submit" className="btn btn-primary" disabled={action.pending}>
            {action.pending ? '만드는 중…' : '만들기'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/** 평문이 보이는 유일한 순간. 창을 닫으면 서버에도 남아 있지 않다. */
function TemporaryPasswordModal({
  issued,
  onClose,
}: {
  issued: { email: string; password: string };
  onClose: () => void;
}): ReactNode {
  const [copied, setCopied] = useState(false);

  return (
    <Modal title="임시 비밀번호" onClose={onClose}>
      <p className="hint">
        <strong>{issued.email}</strong>에게 아래 비밀번호를 직접 전달하세요. 창을 닫으면 다시 볼 수 없습니다.
        <HelpTip
          text="임시 비밀번호는 서버에 저장되지 않아 다시 확인할 수 없습니다. 잃어버렸다면 비밀번호를 다시 재설정하세요."
          label="임시 비밀번호 도움말"
        />
      </p>

      <div className="mt-3 flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
        <code className="flex-1 select-all font-mono text-[15px] tracking-wide text-slate-800">
          {issued.password}
        </code>
        <button
          type="button"
          className="btn btn-tiny"
          onClick={(event) => {
            // 평문 http 의 사내 주소에서도 복사된다(copyText). 못 넣어도 값은 화면에 보인다.
            void copyText(issued.password, event.currentTarget).then(setCopied);
          }}
        >
          {copied ? <Check className="h-3.5 w-3.5" strokeWidth={2} /> : <Copy className="h-3.5 w-3.5" strokeWidth={2} />}
          {copied ? '복사됨' : '복사'}
        </button>
      </div>

      <div className="form-actions">
        <button type="button" className="btn btn-primary" onClick={onClose}>
          전달 완료
        </button>
      </div>
    </Modal>
  );
}
